import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, TextField, Card, CardContent,
  Tooltip, CircularProgress, Alert, Select, MenuItem, InputLabel, FormControl,
  FormControlLabel, Switch, InputAdornment, Snackbar, useMediaQuery, useTheme,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import PercentIcon from '@mui/icons-material/Percent';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Dialog, DialogContent, Popover } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BlockIcon from '@mui/icons-material/Block';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { StyledDialog, inputStyles } from './common';
import DataTable from './common/DataTable';
import { PAISES, PAISES_CIUDADES, PREFIJOS_TELEFONICOS } from '../data/paises_ciudades';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

const sectionTitle = { color: '#134e4a', fontWeight: 700, fontSize: 18, mb: 2 };

function getEmpresaId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return localStorage.getItem('empresa_activa') || user.company_id || user.empresa_id || '1';
  } catch { return '1'; }
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const cardStyle = {
  borderRadius: 4,
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  overflow: 'hidden',
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 16px 40px rgba(236, 72, 153, 0.14)'
  }
};

const gradientAgencia = 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)';
const shadowAgencia = 'rgba(236, 72, 153, 0.4)';


export default function AdminAgencias() {
  // Responsividad: ocultamos columnas no críticas en pantallas pequeñas y medianas
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));   // >= 900px
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));   // >= 1200px
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));   // >= 1536px

  const [agencias, setAgencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [contactosDialog, setContactosDialog] = useState(null);
  const [comisionesPaqDialog, setComisionesPaqDialog] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [paquetes, setPaquetes] = useState([]);
  const [comisionesPaq, setComisionesPaq] = useState([]);
  const [editComision, setEditComision] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm, destructive }
  // Modal "Generar enlace de actualización"
  const [enlaceDialog, setEnlaceDialog] = useState(null); // { agencia, url, expira_at, mensajeWhatsapp, mensajeCorreo }
  const [generandoEnlace, setGenerandoEnlace] = useState(false);
  const [copiado, setCopiado] = useState('');  // 'url' | 'wa' | 'mail'

  // Modal "Invitar nueva agencia"
  const [invitarOpen, setInvitarOpen] = useState(false);
  const [invitarForm, setInvitarForm] = useState({ nombre: '', whatsapp_contacto: '', correo: '' });
  const [invitando, setInvitando] = useState(false);

  // Filtro adicional para estado_registro
  const [filterRegistro, setFilterRegistro] = useState('');

  const handleEnviarInvitacion = async () => {
    if (!invitarForm.nombre.trim()) {
      setSnack({ open: true, msg: 'El nombre es obligatorio', severity: 'error' });
      return;
    }
    setInvitando(true);
    try {
      let userId = null;
      try { const u = JSON.parse(localStorage.getItem('user') || '{}'); userId = u.id || u.user_id || null; } catch {}
      const r = await fetch(`${API_BASE}/turismo/agencias-invitar-nueva`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}) },
        body: JSON.stringify({
          empresa_id: empresaId,
          nombre: invitarForm.nombre.trim(),
          whatsapp_contacto: invitarForm.whatsapp_contacto.trim() || null,
          correo: invitarForm.correo.trim() || null,
          creado_por_user_id: userId,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        setSnack({ open: true, msg: data.detail || 'Error generando invitación', severity: 'error' });
        setInvitando(false);
        return;
      }
      setInvitarOpen(false);
      setInvitarForm({ nombre: '', whatsapp_contacto: '', correo: '' });
      cargar();
      // Reuso el modal de enlace generado
      const origin = window.location.origin;
      const url = `${origin}${data.url_path}`;
      const expiraStr = new Date(data.expira_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
      const mensajeWA =
`Hola, te escribimos de Entretur.
Queremos invitarte a registrarte como agencia vendedora en nuestra plataforma.

Completa tus datos en este enlace:
${url}

Vence el ${expiraStr}.`;
      const mensajeMail =
`Asunto: Invitación a Entretur - completa tus datos de agencia

Hola,

Queremos invitar a tu agencia a unirse a Entretur como vendedora de nuestros tours.

Para completar tu registro, ingresa al siguiente enlace seguro:
${url}

El enlace vence el ${expiraStr}.

En el formulario podrás cargar:
- Razón social, NIT y dirección
- Vendedores con WhatsApp y correo

Tu información será revisada y aprobada por nuestro equipo.

Saludos,
Entretur`;
      setEnlaceDialog({
        agencia: { id: data.agencia_id, nombre: data.agencia_nombre, correo: invitarForm.correo },
        url, expira_at: data.expira_at,
        mensajeWhatsapp: mensajeWA, mensajeCorreo: mensajeMail,
        esInvitacion: true,
      });
    } catch (e) {
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    } finally {
      setInvitando(false);
    }
  };

  // Bulk: enlaces masivos
  const [bulkEnlacesDialog, setBulkEnlacesDialog] = useState(null);  // { enlaces, expira_at }

  const handleBulkGenerarEnlaces = async (ids, clearSelection) => {
    if (!ids.length) return;
    if (!window.confirm(`¿Generar ${ids.length} enlaces de actualización? Los enlaces anteriores vigentes serán revocados.`)) return;
    try {
      let userId = null;
      try { const u = JSON.parse(localStorage.getItem('user') || '{}'); userId = u.id || u.user_id || null; } catch {}
      const r = await fetch(`${API_BASE}/turismo/agencias-bulk-generar-tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}) },
        body: JSON.stringify({ agencia_ids: ids, creado_por_user_id: userId }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        setSnack({ open: true, msg: e.detail || 'Error generando enlaces', severity: 'error' });
        return;
      }
      const data = await r.json();
      const origin = window.location.origin;
      const enlaces = data.enlaces.map(e => ({
        ...e, url: `${origin}${e.url_path}`,
      }));
      setBulkEnlacesDialog({ enlaces, expira_at: data.expira_at });
      clearSelection?.();
      cargar();
    } catch (e) {
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    }
  };

  // Descargar listado CSV (respeta filtros activos)
  const handleDescargarCSV = () => {
    const headers = [
      'Nombre', 'Tipo', 'NIT/Cedula', 'Estado registro', 'Activa',
      'Direccion', 'Pais', 'Ciudad', 'Telefono general', 'Correo agencia',
      'Tipo comision', 'Comision % adulto', 'Comision % niño',
      'Comision $ adulto', 'Comision $ niño', 'Comision legacy %', 'Comision legacy $',
      'Cupo credito', 'Saldo utilizado', 'Saldo disponible',
      'Tiene datafono Entretur', 'Total contactos',
      'Contacto principal', 'WhatsApp principal', 'Correo principal',
      'Datos actualizados', 'Fecha creacion',
    ];
    const escapeCSV = (v) => {
      if (v == null) return '';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const fmtFecha = (s) => {
      if (!s) return '';
      try { return new Date(s).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }); }
      catch { return ''; }
    };
    const rows = agenciasFiltradas.map(a => {
      const principal = (a.contactos || []).find(c => c.es_principal) || (a.contactos || [])[0];
      const saldo = Number(a.saldo_utilizado || 0);
      const cupo = Number(a.cupo_credito || 0);
      const disponible = Math.max(cupo - saldo, 0);
      return [
        a.nombre, a.tipo === 'natural' ? 'Natural' : 'Juridica', a.nit_cedula,
        a.estado_registro || 'activa', a.activa ? 'Si' : 'No',
        a.direccion, a.pais, a.ciudad, a.telefono_general, a.correo,
        a.tipo_comision || 'porcentaje',
        a.porcentaje_comision_adulto ?? '',
        a.porcentaje_comision_nino ?? '',
        a.valor_comision_adulto ?? '',
        a.valor_comision_nino ?? '',
        a.porcentaje_comision ?? '',
        a.valor_comision_fijo ?? '',
        cupo, saldo, disponible,
        a.tiene_datafono_entretour ? 'Si' : 'No',
        a.num_contactos || (a.contactos || []).length || 0,
        principal?.nombre || '',
        principal?.numero_whatsapp || '',
        principal?.correo || '',
        fmtFecha(a.datos_actualizados_at),
        fmtFecha(a.created_at),
      ].map(escapeCSV).join(',');
    });
    const csv = '﻿' + [headers.join(','), ...rows].join('\n');  // BOM UTF-8 para Excel
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agencias-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSnack({ open: true, msg: `${rows.length} agencia(s) descargada(s) en CSV`, severity: 'success' });
  };

  const handleDescartarInvitacion = async (agencia) => {
    if (!window.confirm(`¿Descartar la invitación a "${agencia.nombre}"? Esta acción se puede revertir desde el filtro de descartadas.`)) return;
    try {
      let userId = null;
      try { const u = JSON.parse(localStorage.getItem('user') || '{}'); userId = u.id || u.user_id || null; } catch {}
      const r = await fetch(`${API_BASE}/turismo/agencias/${agencia.id}/descartar-invitacion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}) },
        body: JSON.stringify({ descartada_por_user_id: userId }),
      });
      if (!r.ok) {
        const e = await r.json();
        setSnack({ open: true, msg: e.detail || 'Error', severity: 'error' });
        return;
      }
      setSnack({ open: true, msg: 'Invitación descartada', severity: 'success' });
      cargar();
    } catch (e) {
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    }
  };

  const handleGenerarEnlace = async (agencia) => {
    setGenerandoEnlace(true);
    try {
      let userId = null;
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        userId = u.id || u.user_id || null;
      } catch {}
      const r = await fetch(`${API_BASE}/turismo/agencias/${agencia.id}/generar-token-actualizacion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}) },
        body: JSON.stringify({ creado_por_user_id: userId }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        alert(e.detail || 'Error generando enlace.');
        setGenerandoEnlace(false);
        return;
      }
      const data = await r.json();
      const origin = window.location.origin;
      const url = `${origin}${data.url_path}`;
      const expiraStr = new Date(data.expira_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
      const mensajeWA =
`Hola, te escribo de Entretur.
Para mantener tus datos al día (NIT, dirección, contactos, etc.) usa este enlace:

${url}

Vence el ${expiraStr}. Si necesitas ayuda, respóndenos por acá.`;
      const mensajeMail =
`Asunto: Actualiza los datos de tu agencia en Entretur

Hola,

Para mantener tus datos de contacto y facturación al día en nuestro sistema, te enviamos este enlace seguro y único:

${url}

El enlace vence el ${expiraStr}.

En el formulario podrás:
- Verificar y actualizar razón social, NIT, dirección
- Agregar, editar o eliminar vendedores con su WhatsApp
- Marcar al contacto principal

Tus cambios serán revisados por el equipo de Entretur antes de aplicarse.

Saludos,
Entretur`;
      setEnlaceDialog({
        agencia, url, expira_at: data.expira_at,
        mensajeWhatsapp: mensajeWA, mensajeCorreo: mensajeMail,
      });
    } catch (e) {
      alert('Error de red: ' + (e.message || e));
    } finally {
      setGenerandoEnlace(false);
    }
  };

  const copiar = (texto, tipo) => {
    navigator.clipboard?.writeText(texto).then(() => {
      setCopiado(tipo);
      setTimeout(() => setCopiado(''), 1800);
    }).catch(() => {
      // fallback: select + execCommand
      try {
        const ta = document.createElement('textarea');
        ta.value = texto; document.body.appendChild(ta);
        ta.select(); document.execCommand('copy');
        document.body.removeChild(ta);
        setCopiado(tipo);
        setTimeout(() => setCopiado(''), 1800);
      } catch {}
    });
  };
  // Búsqueda + filtros para tabla
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterCredito, setFilterCredito] = useState('');
  const [filterDatafono, setFilterDatafono] = useState('');
  const searchInputRef = React.useRef(null);

  // Atajo Cmd/Ctrl+K para enfocar el buscador
  React.useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        const input = document.querySelector('input[placeholder*="Buscar agencia"]');
        if (input) input.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Normaliza texto para búsqueda sin acentos
  const norm = (s) => String(s || '').normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '').toLowerCase();

  const agenciasFiltradas = React.useMemo(() => {
    const q = norm(search.trim());
    return agencias.filter(a => {
      // Filtros
      if (filterTipo && a.tipo !== filterTipo) return false;
      if (filterEstado === 'activa' && !a.activa) return false;
      if (filterEstado === 'inactiva' && a.activa) return false;
      if (filterCredito === 'con' && !(a.cupo_credito > 0)) return false;
      if (filterCredito === 'sin' && a.cupo_credito > 0) return false;
      if (filterCredito === 'saldo_pendiente' && !(Number(a.saldo_utilizado || 0) > 0)) return false;
      if (filterDatafono === 'si' && !a.tiene_datafono_entretour) return false;
      if (filterDatafono === 'no' && a.tiene_datafono_entretour) return false;
      // Filtro por estado de registro
      const estadoReg = a.estado_registro || 'activa';
      if (filterRegistro === 'activa' && estadoReg !== 'activa') return false;
      if (filterRegistro === 'invitada' && estadoReg !== 'invitada') return false;
      if (!filterRegistro && estadoReg === 'descartada') return false;  // por defecto ocultar descartadas
      // Búsqueda multi-campo
      if (!q) return true;
      const contactosTexto = (a.contactos || []).map(c =>
        `${c.nombre || ''} ${c.numero_whatsapp || ''} ${c.correo || ''}`
      ).join(' ');
      const blob = norm([
        a.nombre, a.nit_cedula, a.ciudad, a.pais,
        a.telefono_general, a.correo, contactosTexto,
      ].join(' '));
      return blob.includes(q);
    });
  }, [agencias, search, filterTipo, filterEstado, filterCredito, filterDatafono, filterRegistro]);
  const empresaId = getEmpresaId();

  const notify = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const confirmar = (title, message, onConfirm, destructive = true) => {
    setConfirmDialog({ title, message, onConfirm, destructive });
  };

  function emptyForm() {
    return {
      nombre: '', tipo: 'juridica', nit_cedula: '',
      direccion: '', pais: 'Colombia', ciudad: '',
      telefono_local: '',
      correo: '',
      cupo_credito: '0',
      tiene_datafono_entretour: false,
      activa: true
    };
  }

  // Separa un teléfono guardado en prefijo+local (si empieza con prefijo conocido)
  function splitTelefono(tel) {
    if (!tel) return { prefijo: '', local: '' };
    const t = tel.trim();
    // Ordenar prefijos por longitud descendente para que +593 matche antes que +59
    const prefijos = Object.values(PREFIJOS_TELEFONICOS).filter(Boolean).sort((a, b) => b.length - a.length);
    for (const pref of prefijos) {
      if (t.startsWith(pref)) {
        return { prefijo: pref, local: t.slice(pref.length).trim() };
      }
    }
    return { prefijo: '', local: t };
  }

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}&solo_activas=false`, { headers: getAuthHeaders() });
      setAgencias(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}&solo_activos=true`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(setPaquetes).catch(() => {});
  }, [empresaId]);

  const cargarComisiones = async (agenciaId) => {
    try {
      const res = await fetch(`${API_BASE}/turismo/agencias/${agenciaId}/comisiones-paquete`, { headers: getAuthHeaders() });
      setComisionesPaq(await res.json());
    } catch { setComisionesPaq([]); }
  };

  const handleOpen = (item = null) => {
    setEditItem(item);
    setEditComision(null);
    if (item) { cargarComisiones(item.id); } else { setComisionesPaq([]); }
    const { local } = splitTelefono(item?.telefono_general || '');
    setForm(item ? {
      nombre: item.nombre || '',
      tipo: item.tipo || 'juridica',
      nit_cedula: item.nit_cedula || '',
      direccion: item.direccion || '',
      pais: item.pais || 'Colombia',
      ciudad: item.ciudad || '',
      telefono_local: local,
      correo: item.correo || '',
      cupo_credito: String(item.cupo_credito || 0),
      tiene_datafono_entretour: item.tiene_datafono_entretour === true,
      activa: item.activa !== false,
    } : emptyForm());
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const body = {
      ...form,
      cupo_credito: parseFloat(form.cupo_credito) || 0,
      nit_cedula: form.nit_cedula.trim() || null,
      direccion: form.direccion.trim() || null,
      pais: form.pais || null,
      ciudad: form.ciudad || null,
      telefono_general: form.telefono_local.trim()
        ? `${PREFIJOS_TELEFONICOS[form.pais] || ''} ${form.telefono_local.trim()}`.trim()
        : null,
      correo: form.correo.trim() || null,
    };
    delete body.telefono_local;
    if (!editItem) body.empresa_id = empresaId;
    const url = editItem ? `${API_BASE}/turismo/agencias/${editItem.id}` : `${API_BASE}/turismo/agencias`;
    const res = await fetch(url, { method: editItem ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      notify(`No se pudo guardar: ${err.detail || 'error desconocido'}`, 'error');
      return;
    }
    setDialogOpen(false);
    notify(editItem ? 'Agencia actualizada' : 'Agencia creada');
    cargar();
  };

  const handleGuardarComision = async () => {
    if (!editItem || !editComision) return;
    const va = parseFloat(editComision.valor_adulto) || 0;
    const vn = parseFloat(editComision.valor_nino)   || 0;
    const isPct = editComision.tipo_comision === 'porcentaje';
    const body = {
      paquete_id: editComision.paquete_id,
      tipo_comision: editComision.tipo_comision,
      // Nuevos campos (diferenciados por tipo de pasajero)
      porcentaje_comision_adulto: isPct ? va : null,
      porcentaje_comision_nino:   isPct ? vn : null,
      valor_comision_adulto:      isPct ? null : va,
      valor_comision_nino:        isPct ? null : vn,
      // Legacy: dejar el valor del adulto como fallback (retrocompat con código viejo)
      porcentaje_comision: isPct ? va : null,
      valor_comision_fijo: isPct ? null : va,
    };
    const res = await fetch(`${API_BASE}/turismo/agencias/${editItem.id}/comisiones-paquete`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
    });
    if (res.ok) { notify('Comisión guardada'); setEditComision(null); cargarComisiones(editItem.id); }
  };

  const handleEliminarComision = (comId) => {
    confirmar('Eliminar comisión', 'El tour quedará sin comisión configurada para esta agencia.', async () => {
      await fetch(`${API_BASE}/turismo/agencias/comisiones-paquete/${comId}`, { method: 'DELETE', headers: getAuthHeaders() });
      notify('Comisión eliminada');
      if (editItem) cargarComisiones(editItem.id);
    });
  };

  const handleDelete = (id) => {
    const ag = agencias.find(a => a.id === id);
    confirmar('Eliminar agencia', `¿Eliminar "${ag?.nombre || 'esta agencia'}"? Esta acción no se puede deshacer.`, async () => {
      await fetch(`${API_BASE}/turismo/agencias/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      cargar();
    });
  };

  const handleToggleActiva = async (agencia) => {
    await fetch(`${API_BASE}/turismo/agencias/${agencia.id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ activa: !agencia.activa }),
    });
    cargar();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ ...sectionTitle, mb: 0.5 }}>Agencias Vendedoras</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            Gestiona las agencias que revenden tus paquetes y sus comisiones.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDescargarCSV}
            disabled={agenciasFiltradas.length === 0}
            sx={{
              borderColor: '#0f766e', color: '#0f766e', borderRadius: 3, textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: '#134e4a', bgcolor: '#f0fdfa' },
            }}>
            Descargar CSV ({agenciasFiltradas.length})
          </Button>
          <Button variant="outlined" startIcon={<MailOutlineIcon />} onClick={() => setInvitarOpen(true)}
            sx={{
              borderColor: '#0284c7', color: '#0284c7', borderRadius: 3, textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: '#0369a1', bgcolor: '#eff6ff' },
            }}>
            Invitar nueva agencia
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
            sx={{ background: gradientAgencia, borderRadius: 3, textTransform: 'none', fontWeight: 600, boxShadow: `0 8px 20px ${shadowAgencia}` }}>
            Nueva Agencia
          </Button>
        </Box>
      </Box>

      {/* Tabla con búsqueda, filtros y paginación (escala a 700+ agencias) */}
      <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 1 }}>
        {agenciasFiltradas.length} de {agencias.length} agencias · Atajo: <kbd style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: 10 }}>⌘K</kbd> / <kbd style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: 10 }}>Ctrl+K</kbd>
      </Typography>
      <DataTable
        rows={agenciasFiltradas}
        loading={loading}
        rowKey="id"
        pageSize={50}
        onRowClick={(a) => handleOpen(a)}
        bulkActions={[
          {
            label: 'Generar enlaces de actualización',
            icon: <ShareIcon sx={{ fontSize: 16 }} />,
            onClick: (ids, clearSel) => handleBulkGenerarEnlaces(ids, clearSel),
          },
        ]}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Buscar agencia, NIT, ciudad, contacto, teléfono…',
        }}
        filters={[
          { key: 'tipo',     label: 'Tipo',     value: filterTipo,     onChange: setFilterTipo,
            options: [{ value: 'juridica', label: 'Jurídica' }, { value: 'natural', label: 'Natural' }] },
          { key: 'estado',   label: 'Estado',   value: filterEstado,   onChange: setFilterEstado,
            options: [{ value: 'activa', label: 'Activas' }, { value: 'inactiva', label: 'Inactivas' }] },
          { key: 'credito',  label: 'Crédito',  value: filterCredito,  onChange: setFilterCredito,
            options: [
              { value: 'con',              label: 'Con cupo' },
              { value: 'sin',              label: 'Sin cupo' },
              { value: 'saldo_pendiente',  label: 'Con saldo pendiente' },
            ] },
          { key: 'datafono', label: 'Datáfono', value: filterDatafono, onChange: setFilterDatafono,
            options: [{ value: 'si', label: 'Tiene' }, { value: 'no', label: 'No tiene' }] },
          { key: 'registro', label: 'Onboarding', value: filterRegistro, onChange: setFilterRegistro,
            options: [
              { value: 'activa',     label: 'Operativas' },
              { value: 'invitada',   label: 'Invitadas (pendientes)' },
              { value: 'descartada', label: 'Descartadas' },
            ] },
        ]}
        emptyState={
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>
              {agencias.length === 0 ? 'No hay agencias registradas' : 'Ningún resultado'}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              {agencias.length === 0 ? 'Crea tu primera agencia vendedora' : 'Prueba con otra búsqueda o limpia los filtros'}
            </Typography>
          </Box>
        }
        columns={[
          // Plan responsivo:
          //   xs/sm (<md): solo Agencia + Estado + Acciones (3 columnas + scroll horizontal)
          //   md   (≥900):  + Comisión + Contacto principal (5)
          //   lg   (≥1200): + Tipo + Ubicación + Crédito (8)
          //   xl   (≥1536): todas (9, incluye Datos verificados)
          {
            key: 'nombre', label: 'Agencia', sortable: true,
            sortValue: a => (a.nombre || '').toLowerCase(),
            render: a => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220, opacity: a.activa ? 1 : 0.55 }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: 2, background: gradientAgencia,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', flexShrink: 0,
                }}>
                  {a.tipo === 'natural' ? <PersonIcon sx={{ fontSize: 16 }} /> : <BusinessIcon sx={{ fontSize: 16 }} />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#134e4a', lineHeight: 1.2 }}>
                    {a.nombre}
                  </Typography>
                  {a.nit_cedula && (
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                      {a.tipo === 'natural' ? 'CC' : 'NIT'}: {a.nit_cedula}
                    </Typography>
                  )}
                </Box>
              </Box>
            ),
          },
          isLg && {
            key: 'tipo', label: 'Tipo', sortable: true,
            render: a => (
              <Chip size="small" label={a.tipo === 'natural' ? 'Natural' : 'Jurídica'}
                sx={{ fontSize: 10, height: 20, fontWeight: 600,
                      bgcolor: a.tipo === 'natural' ? '#fef3c7' : '#dbeafe',
                      color:   a.tipo === 'natural' ? '#92400e' : '#1d4ed8' }} />
            ),
          },
          isLg && {
            key: 'ubicacion', label: 'Ubicación', sortable: true,
            sortValue: a => (a.ciudad || '').toLowerCase(),
            render: a => (
              <Box>
                <Typography sx={{ fontSize: 12, color: '#475569' }}>
                  {[a.ciudad, a.pais].filter(Boolean).join(', ') || '—'}
                </Typography>
                {a.telefono_general && (
                  <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>📞 {a.telefono_general}</Typography>
                )}
              </Box>
            ),
          },
          isMd && {
            key: 'contactos', label: 'Contacto principal',
            render: a => {
              const principal = (a.contactos || []).find(c => c.es_principal);
              if (!principal) return <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>;
              return (
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#134e4a' }}>{principal.nombre}</Typography>
                  {principal.numero_whatsapp && (
                    <Typography sx={{ fontSize: 10, color: '#10b981' }}>📱 {principal.numero_whatsapp}</Typography>
                  )}
                  {a.num_contactos > 1 && (
                    <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>+ {a.num_contactos - 1} más</Typography>
                  )}
                </Box>
              );
            },
          },
          isMd && {
            key: 'comision', label: 'Comisión por PAX',
            render: a => <ComisionesPorTourCell agenciaId={a.id} />,
          },
          isLg && {
            key: 'credito', label: 'Crédito · Saldo', sortable: true,
            sortValue: a => Number(a.saldo_utilizado || 0),
            render: a => {
              if (!(a.cupo_credito > 0)) {
                return <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Sin cupo</Typography>;
              }
              const usado = Number(a.saldo_utilizado || 0);
              const cupo = Number(a.cupo_credito || 0);
              const pct = cupo > 0 ? Math.min((usado / cupo) * 100, 100) : 0;
              const colorBarra = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#10b981';
              return (
                <Box sx={{ minWidth: 130 }}>
                  <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
                    ${new Intl.NumberFormat('es-CO').format(usado)} / ${new Intl.NumberFormat('es-CO').format(cupo)}
                  </Typography>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0', overflow: 'hidden', mt: 0.5 }}>
                    <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: colorBarra }} />
                  </Box>
                </Box>
              );
            },
          },
          isMd && {
            key: 'datos_actualizados', label: 'Datos verificados', sortable: true,
            sortValue: a => a.datos_actualizados_at ? new Date(a.datos_actualizados_at).getTime() : 0,
            render: a => {
              // Para invitadas: aún no aplica
              if (a.estado_registro === 'invitada') {
                return <Chip size="small" label="Esperando datos"
                  sx={{ fontSize: 10, height: 18, fontWeight: 600, bgcolor: '#e2e8f0', color: '#475569' }} />;
              }
              // Si nunca actualizó, mostrar "Sin actualizar"
              if (!a.datos_actualizados_at) {
                return <Chip size="small" label="Sin actualizar"
                  sx={{ fontSize: 10, height: 18, fontWeight: 600, bgcolor: '#fef2f2', color: '#991b1b' }} />;
              }
              const fechaIso = a.datos_actualizados_at;
              const fecha = new Date(fechaIso);
              const fechaCorta = fecha.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
              const meses = Math.floor((Date.now() - fecha.getTime()) / (1000 * 60 * 60 * 24 * 30.4));
              const color = meses > 12 ? '#dc2626' : meses > 6 ? '#f59e0b' : '#16a34a';
              const tooltip = meses === 0 ? 'Este mes' : meses === 1 ? 'Hace 1 mes' : `Hace ${meses} meses`;
              return (
                <Tooltip title={tooltip} arrow>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 14, color }} />
                    <Typography sx={{ fontSize: 11, color, fontWeight: 700 }}>
                      {fechaCorta}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            },
          },
          {
            key: 'estado', label: 'Estado',
            render: a => {
              const reg = a.estado_registro || 'activa';
              if (reg === 'invitada') {
                return (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip size="small" label="Invitada · sin completar"
                      sx={{ fontSize: 10, height: 20, fontWeight: 700, bgcolor: '#e2e8f0', color: '#475569' }} />
                  </Box>
                );
              }
              if (reg === 'descartada') {
                return (
                  <Chip size="small" label="Descartada"
                    sx={{ fontSize: 10, height: 20, fontWeight: 700, bgcolor: '#fef2f2', color: '#7f1d1d' }} />
                );
              }
              return (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip size="small" label={a.activa ? 'Activa' : 'Inactiva'}
                    sx={{ fontSize: 10, height: 20, fontWeight: 700,
                          bgcolor: a.activa ? '#dcfce7' : '#fee2e2',
                          color:   a.activa ? '#166534' : '#991b1b' }} />
                  {a.tiene_datafono_entretour && (
                    <Chip size="small" label="Datáfono"
                      sx={{ fontSize: 9, height: 18, bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }} />
                  )}
                </Box>
              );
            },
          },
          {
            key: 'acciones', label: 'Acciones', align: 'right',
            render: a => (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
                <Tooltip title={`Contactos (${a.num_contactos})`}>
                  <IconButton size="small" onClick={() => setContactosDialog(a)} sx={{ color: '#be185d' }}>
                    <PeopleIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Comisiones por paquete">
                  <IconButton size="small" onClick={() => setComisionesPaqDialog(a)} sx={{ color: '#f59e0b' }}>
                    <PercentIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={a.estado_registro === 'invitada' ? 'Regenerar enlace de invitación' : 'Generar enlace para que la agencia actualice sus datos'}>
                  <IconButton size="small" onClick={() => handleGenerarEnlace(a)} sx={{ color: '#0284c7' }}>
                    <ShareIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                {a.estado_registro === 'invitada' && (
                  <Tooltip title="Descartar invitación">
                    <IconButton size="small" onClick={() => handleDescartarInvitacion(a)} sx={{ color: '#64748b' }}>
                      <BlockIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={a.activa ? 'Desactivar' : 'Activar'}>
                  <Switch size="small" checked={a.activa} onChange={() => handleToggleActiva(a)} sx={{ ml: 0.25 }} />
                </Tooltip>
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => handleOpen(a)} sx={{ color: '#0d9488' }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar">
                  <IconButton size="small" onClick={() => handleDelete(a.id)} color="error">
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
          },
        ].filter(Boolean)}
      />

      {/* Diálogo crear/editar agencia */}
      <StyledDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        title={editItem ? 'Editar Agencia' : 'Nueva Agencia'}
        icon={<BusinessIcon />} variant="compact" maxWidth="md">
        <StyledDialog.Body compact>

          {/* Grid 2 columnas en desktop, 1 en móvil */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>

            {/* ═══ COL IZQUIERDA ═══ */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

              {/* Identificación */}
              <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Identificación</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField sx={inputStyles} label="Nombre" fullWidth required size="small"
                    value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl sx={{ ...inputStyles, flex: 1 }} size="small">
                      <InputLabel>Tipo</InputLabel>
                      <Select value={form.tipo} label="Tipo" onChange={e => setForm({ ...form, tipo: e.target.value })}>
                        <MenuItem value="juridica">Jurídica</MenuItem>
                        <MenuItem value="natural">Natural</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField sx={{ ...inputStyles, flex: 1 }} size="small"
                      label={form.tipo === 'natural' ? 'Cédula' : 'NIT'} value={form.nit_cedula}
                      onChange={e => setForm({ ...form, nit_cedula: e.target.value })} />
                  </Box>
                </Box>
              </Box>

              {/* Ubicación */}
              <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <LocationOnIcon sx={{ fontSize: 16, color: '#0891b2' }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Ubicación</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl sx={{ ...inputStyles, flex: 1 }} size="small">
                      <InputLabel>País</InputLabel>
                      <Select value={form.pais} label="País"
                        onChange={e => setForm({ ...form, pais: e.target.value, ciudad: '' })}>
                        {PAISES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ ...inputStyles, flex: 1 }} size="small" disabled={!form.pais}>
                      <InputLabel>Ciudad</InputLabel>
                      <Select value={form.ciudad} label="Ciudad"
                        onChange={e => setForm({ ...form, ciudad: e.target.value })}>
                        {[...(PAISES_CIUDADES[form.pais] || [])].sort((a, b) => a.localeCompare(b, 'es')).map(c => (
                          <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <TextField sx={inputStyles} label="Dirección" fullWidth size="small"
                    value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })}
                    placeholder="Calle, carrera, barrio" />
                </Box>
              </Box>

              {/* Contacto */}
              <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <PhoneIcon sx={{ fontSize: 16, color: '#10b981' }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Contacto</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField sx={inputStyles} label="Teléfono" fullWidth size="small"
                    value={form.telefono_local}
                    onChange={e => setForm({ ...form, telefono_local: e.target.value })}
                    placeholder="300 000 0000"
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontWeight: 600, color: '#10b981', fontSize: 12 }}>
                          {PREFIJOS_TELEFONICOS[form.pais] || ''}
                        </Typography>
                      </InputAdornment>
                    ) }} />
                  <TextField sx={inputStyles} label="Correo electrónico" type="email" fullWidth size="small"
                    value={form.correo} onChange={e => setForm({ ...form, correo: e.target.value })}
                    placeholder="contacto@agencia.com" />
                </Box>
              </Box>
            </Box>

            {/* ═══ COL DERECHA ═══ */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

              {/* Financiero */}
              <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <PercentIcon sx={{ fontSize: 16, color: '#1d4ed8' }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Financiero</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField sx={inputStyles} label="Cupo crédito (COP)" type="number" fullWidth size="small"
                    value={form.cupo_credito}
                    onChange={e => setForm({ ...form, cupo_credito: e.target.value })}
                    inputProps={{ min: 0, step: 10000 }}
                    helperText="0 = pago inmediato por reserva" />
                  <FormControlLabel sx={{ ml: 0 }}
                    control={<Switch size="small" checked={form.tiene_datafono_entretour} onChange={e => setForm({ ...form, tiene_datafono_entretour: e.target.checked })} />}
                    label={<Typography sx={{ fontSize: 11 }}>Datáfono Entretour</Typography>} />
                </Box>
              </Box>
            </Box>
            {/* cierre col derecha + cierre grid */}
          </Box>

          {/* ═══ SECCIÓN 5: COMISIONES POR TOUR (full width) ═══ */}
          {editItem && (
            <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#fdf2f8', border: '1px solid #fce7f3' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <PercentIcon sx={{ fontSize: 16, color: '#be185d' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Comisiones por tour</Typography>
                <Chip size="small" label={`${comisionesPaq.length}/${paquetes.length}`}
                  sx={{ ml: 'auto', height: 18, fontSize: 10, bgcolor: '#be185d', color: '#fff' }} />
              </Box>

              {/* Tabla compacta con scroll interno */}
              {comisionesPaq.length > 0 && (
                <Box sx={{ maxHeight: 180, overflowY: 'auto', borderRadius: 1.5, border: '1px solid #fce7f3', bgcolor: '#fff', mb: 1 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: '#fdf2f8', zIndex: 1 }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#64748b', fontSize: 10, borderBottom: '1px solid #fce7f3' }}>Tour</th>
                        <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 600, color: '#64748b', fontSize: 10, borderBottom: '1px solid #fce7f3' }}>Tipo</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b', fontSize: 10, borderBottom: '1px solid #fce7f3' }}>Valor</th>
                        <th style={{ width: 56, borderBottom: '1px solid #fce7f3' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {comisionesPaq.map(c => {
                        const isPct = c.tipo_comision === 'porcentaje';
                        // Nuevos campos tienen prioridad; fallback a legacy si no están.
                        const valAdulto = isPct
                          ? (c.porcentaje_comision_adulto ?? c.porcentaje_comision ?? 0)
                          : (c.valor_comision_adulto ?? c.valor_comision_fijo ?? 0);
                        const valNino = isPct
                          ? (c.porcentaje_comision_nino ?? c.porcentaje_comision ?? 0)
                          : (c.valor_comision_nino ?? c.valor_comision_fijo ?? 0);
                        const fmtVal = (v) => isPct ? `${v}%` : `$${new Intl.NumberFormat('es-CO').format(v)}`;
                        return (
                        <tr key={c.id} style={{ borderBottom: '1px solid #fef2f8' }}>
                          <td style={{ padding: '5px 8px', fontWeight: 500 }}>{c.paquete_nombre}</td>
                          <td style={{ padding: '5px 4px', textAlign: 'center', color: '#64748b' }}>
                            {isPct ? '%' : '$/pax'}
                          </td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: '#be185d', fontSize: 11 }}>
                            A: {fmtVal(valAdulto)}<br/>N: {fmtVal(valNino)}
                          </td>
                          <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                            <IconButton size="small" onClick={() => setEditComision({
                              paquete_id: c.paquete_id, tipo_comision: c.tipo_comision,
                              valor_adulto: String(valAdulto),
                              valor_nino: String(valNino),
                              nombre: c.paquete_nombre,
                            })} sx={{ p: 0.25 }}><EditIcon sx={{ fontSize: 13 }} /></IconButton>
                            <IconButton size="small" onClick={() => handleEliminarComision(c.id)} sx={{ p: 0.25, color: '#ef4444' }}>
                              <DeleteIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              )}

              {/* Dropdown para agregar comisión a un tour */}
              {(() => {
                const sinComision = paquetes.filter(p => !comisionesPaq.find(c => c.paquete_id === p.id));
                return sinComision.length > 0 && !editComision ? (
                  <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: 1.5 }}>
                    <Select displayEmpty value=""
                      onChange={e => {
                        const p = sinComision.find(x => x.id === e.target.value);
                        if (p) setEditComision({ paquete_id: p.id, tipo_comision: 'valor_fijo', valor_adulto: '', valor_nino: '', nombre: p.nombre });
                      }}
                      sx={{ fontSize: 12, color: '#be185d', '& .MuiSelect-select': { py: 0.75 } }}
                      renderValue={() => `+ Agregar comisión (${sinComision.length} tours disponibles)`}>
                      {sinComision.map(p => (
                        <MenuItem key={p.id} value={p.id} sx={{ fontSize: 12 }}>{p.nombre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null;
              })()}

              {/* Form inline de edición */}
              {editComision && (
                <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, bgcolor: '#fff', border: '2px solid #be185d',
                  boxShadow: '0 4px 12px rgba(190,24,93,0.12)' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#be185d', mb: 0.75 }}>
                    {editComision.nombre}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Select size="small" value={editComision.tipo_comision}
                      onChange={e => setEditComision({ ...editComision, tipo_comision: e.target.value, valor_adulto: '', valor_nino: '' })}
                      sx={{ fontSize: 12, height: 32, minWidth: 100 }}>
                      <MenuItem value="valor_fijo" sx={{ fontSize: 12 }}>$/persona</MenuItem>
                      <MenuItem value="porcentaje" sx={{ fontSize: 12 }}>%</MenuItem>
                    </Select>
                    <TextField size="small" type="number" value={editComision.valor_adulto}
                      onChange={e => setEditComision({ ...editComision, valor_adulto: e.target.value })}
                      label="Adulto" InputLabelProps={{ shrink: true }}
                      placeholder={editComision.tipo_comision === 'valor_fijo' ? '100000' : '15'}
                      sx={{ width: 96, '& input': { fontSize: 12 } }}
                      inputProps={{ min: 0, step: editComision.tipo_comision === 'valor_fijo' ? 1000 : 0.1 }} />
                    <TextField size="small" type="number" value={editComision.valor_nino}
                      onChange={e => setEditComision({ ...editComision, valor_nino: e.target.value })}
                      label="Niño" InputLabelProps={{ shrink: true }}
                      placeholder={editComision.tipo_comision === 'valor_fijo' ? '50000' : '10'}
                      sx={{ width: 96, '& input': { fontSize: 12 } }}
                      inputProps={{ min: 0, step: editComision.tipo_comision === 'valor_fijo' ? 1000 : 0.1 }} />
                    <Button size="small" variant="contained" onClick={handleGuardarComision}
                      disabled={!editComision.valor_adulto && !editComision.valor_nino}
                      sx={{ fontSize: 11, textTransform: 'none', bgcolor: '#be185d', '&:hover': { bgcolor: '#9f1239' }, minWidth: 0, px: 1.5 }}>
                      OK
                    </Button>
                    <Button size="small" onClick={() => setEditComision(null)}
                      sx={{ fontSize: 11, textTransform: 'none', color: '#64748b', minWidth: 0, px: 1 }}>✕</Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {!editItem && (
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fdf2f8', border: '1px dashed #fce7f3' }}>
              <Typography sx={{ fontSize: 11, color: '#be185d' }}>
                💡 Las comisiones por tour se configuran después de crear la agencia.
              </Typography>
            </Box>
          )}

          {/* ═══ ESTADO ═══ */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #e2e8f0' }}>
            <FormControlLabel
              control={<Switch size="small" checked={form.activa} onChange={e => setForm({ ...form, activa: e.target.checked })} />}
              label={<Typography sx={{ fontSize: 12, fontWeight: 600, color: form.activa ? '#10b981' : '#ef4444' }}>
                {form.activa ? 'Agencia Activa' : 'Agencia Inactiva'}
              </Typography>} />
          </Box>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setDialogOpen(false)} />
          <StyledDialog.PrimaryButton fullWidth onClick={handleSave}
            disabled={!form.nombre}>
            {editItem ? 'Actualizar' : 'Crear'}
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* Diálogo de contactos */}
      {contactosDialog && (
        <ContactosDialog agencia={contactosDialog}
          onClose={() => { setContactosDialog(null); cargar(); }}
          notify={notify} confirmar={confirmar} />
      )}


      {/* Diálogo de confirmación elegante */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}
        PaperProps={{ sx: { borderRadius: 4, maxWidth: 380, overflow: 'hidden' } }}>
        {confirmDialog && (
          <>
            <Box sx={{ background: confirmDialog.destructive !== false
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: 28, color: '#fff' }} />
              </Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16, textAlign: 'center' }}>
                {confirmDialog.title}
              </Typography>
            </Box>
            <DialogContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: '#475569', fontSize: 14, mb: 3, lineHeight: 1.6 }}>
                {confirmDialog.message}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button fullWidth variant="outlined" onClick={() => setConfirmDialog(null)}
                  sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600, color: '#64748b', borderColor: '#e2e8f0',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' } }}>
                  Cancelar
                </Button>
                <Button fullWidth variant="contained" onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
                  sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600,
                    bgcolor: confirmDialog.destructive !== false ? '#ef4444' : '#f59e0b',
                    '&:hover': { bgcolor: confirmDialog.destructive !== false ? '#dc2626' : '#d97706' },
                    boxShadow: `0 4px 12px ${confirmDialog.destructive !== false ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}` }}>
                  Confirmar
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled"
          sx={{ fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
          onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Modal: Enlaces generados masivamente — con 3 modos */}
      {bulkEnlacesDialog && (
        <BulkEnlacesModal
          data={bulkEnlacesDialog}
          copiado={copiado}
          onCopiar={copiar}
          onClose={() => setBulkEnlacesDialog(null)}
        />
      )}

      {/* Modal: Invitar nueva agencia */}
      <StyledDialog open={invitarOpen} onClose={() => !invitando && setInvitarOpen(false)}
        title="Invitar nueva agencia"
        icon={<MailOutlineIcon />} variant="compact" maxWidth="xs">
        <StyledDialog.Body compact>
          <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.5 }}>
            Solo necesitas el nombre. La agencia completará el resto desde el portal.
          </Typography>
          <TextField sx={inputStyles} size="small" fullWidth label="Nombre de la agencia *"
            value={invitarForm.nombre}
            onChange={e => setInvitarForm(f => ({ ...f, nombre: e.target.value }))}
            placeholder="Wooly Travel" />
          <TextField sx={inputStyles} size="small" fullWidth label="WhatsApp del contacto (opcional)"
            value={invitarForm.whatsapp_contacto}
            onChange={e => setInvitarForm(f => ({ ...f, whatsapp_contacto: e.target.value }))}
            placeholder="+57 300 1234567" />
          <TextField sx={inputStyles} size="small" fullWidth label="Correo (opcional)"
            value={invitarForm.correo}
            onChange={e => setInvitarForm(f => ({ ...f, correo: e.target.value }))}
            placeholder="info@agencia.com" type="email" />
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setInvitarOpen(false)} disabled={invitando} />
          <StyledDialog.PrimaryButton fullWidth onClick={handleEnviarInvitacion} loading={invitando}>
            Generar invitación
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* Modal: Enlace de actualización generado */}
      {enlaceDialog && (
        <StyledDialog open onClose={() => setEnlaceDialog(null)}
          title="Enlace de actualización generado"
          subtitle={enlaceDialog.agencia.nombre}
          icon={<ShareIcon />}
          maxWidth="sm">
          <StyledDialog.Body>
            <Alert severity="success" sx={{ fontSize: 13, mb: 2 }}>
              Copia el enlace y pégalo en tu WhatsApp o correo personal para enviárselo a la agencia.
              Vence el <b>{new Date(enlaceDialog.expira_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}</b>.
            </Alert>

            {/* URL */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
              Enlace único
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2.5, alignItems: 'stretch' }}>
              <TextField fullWidth size="small" value={enlaceDialog.url}
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 11 } }} />
              <Button variant="contained" disableElevation
                startIcon={copiado === 'url' ? <CheckIcon /> : <ContentCopyIcon />}
                onClick={() => copiar(enlaceDialog.url, 'url')}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap',
                      bgcolor: copiado === 'url' ? '#16a34a' : '#0284c7',
                      '&:hover': { bgcolor: copiado === 'url' ? '#15803d' : '#0369a1' } }}>
                {copiado === 'url' ? 'Copiado' : 'Copiar'}
              </Button>
            </Box>

            {/* Mensaje WhatsApp */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
              <WhatsAppIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5, color: '#10b981' }} />
              Mensaje sugerido para WhatsApp
            </Typography>
            <TextField fullWidth multiline minRows={5} size="small" value={enlaceDialog.mensajeWhatsapp}
              InputProps={{ sx: { fontSize: 12 } }}
              onChange={(e) => setEnlaceDialog(d => ({ ...d, mensajeWhatsapp: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2.5 }}>
              <Button fullWidth size="small" variant="outlined"
                startIcon={copiado === 'wa' ? <CheckIcon /> : <ContentCopyIcon />}
                onClick={() => copiar(enlaceDialog.mensajeWhatsapp, 'wa')}
                sx={{ textTransform: 'none', color: copiado === 'wa' ? '#16a34a' : '#10b981',
                      borderColor: copiado === 'wa' ? '#16a34a' : '#10b981' }}>
                {copiado === 'wa' ? 'Copiado' : 'Copiar mensaje'}
              </Button>
              <Button fullWidth size="small" variant="contained" disableElevation
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(enlaceDialog.mensajeWhatsapp)}`, '_blank')}
                sx={{ textTransform: 'none', bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
                Abrir WhatsApp Web
              </Button>
            </Box>

            {/* Mensaje correo */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
              ✉️ Mensaje sugerido para correo
            </Typography>
            <TextField fullWidth multiline minRows={5} size="small" value={enlaceDialog.mensajeCorreo}
              InputProps={{ sx: { fontSize: 12, fontFamily: 'monospace' } }}
              onChange={(e) => setEnlaceDialog(d => ({ ...d, mensajeCorreo: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button fullWidth size="small" variant="outlined"
                startIcon={copiado === 'mail' ? <CheckIcon /> : <ContentCopyIcon />}
                onClick={() => copiar(enlaceDialog.mensajeCorreo, 'mail')}
                sx={{ textTransform: 'none', color: copiado === 'mail' ? '#16a34a' : '#0284c7',
                      borderColor: copiado === 'mail' ? '#16a34a' : '#0284c7' }}>
                {copiado === 'mail' ? 'Copiado' : 'Copiar mensaje'}
              </Button>
              <Button fullWidth size="small" variant="contained" disableElevation
                onClick={() => {
                  const subject = 'Actualiza los datos de tu agencia en Entretur';
                  const body = enlaceDialog.mensajeCorreo.replace(/^Asunto:[^\n]*\n+/i, '');
                  const correoDestino = enlaceDialog.agencia.correo || '';
                  window.location.href = `mailto:${correoDestino}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                }}
                sx={{ textTransform: 'none', bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' } }}>
                Abrir cliente de correo
              </Button>
            </Box>
          </StyledDialog.Body>
          <StyledDialog.Actions>
            <StyledDialog.PrimaryButton onClick={() => setEnlaceDialog(null)}>Cerrar</StyledDialog.PrimaryButton>
          </StyledDialog.Actions>
        </StyledDialog>
      )}
    </Box>
  );
}


// ============================================================================
// DIÁLOGO: CONTACTOS DE AGENCIA
// ============================================================================
function ContactosDialog({ agencia, onClose, notify, confirmar }) {
  const [contactos, setContactos] = useState(agencia.contactos || []);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ nombre: '', numero_local: '', correo: '', cargo: '', activo: true });
  const [editing, setEditing] = useState(false);

  const prefijoAgencia = PREFIJOS_TELEFONICOS[agencia.pais] || '+57';

  // Separa un teléfono en prefijo y local
  const splitTel = (tel) => {
    if (!tel) return '';
    const t = tel.trim();
    const prefijos = Object.values(PREFIJOS_TELEFONICOS).filter(Boolean).sort((a, b) => b.length - a.length);
    for (const pref of prefijos) {
      if (t.startsWith(pref)) return t.slice(pref.length).trim();
    }
    return t;
  };

  const cargar = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/turismo/agencias/${agencia.id}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setContactos(data.contactos || []);
    } catch (e) { console.error(e); }
  }, [agencia.id]);

  const handleOpenForm = (item = null) => {
    setEditItem(item);
    setForm(item ? {
      nombre: item.nombre,
      numero_local: splitTel(item.numero_whatsapp || ''),
      correo: item.correo || '', cargo: item.cargo || '',
      identificacion: item.identificacion || '',
      es_principal: item.es_principal === true,
      activo: item.activo !== false,
    } : { nombre: '', numero_local: '', correo: '', cargo: '', identificacion: '', es_principal: contactos.length === 0, activo: true });
    setEditing(true);
  };

  const handleSave = async () => {
    const body = {
      nombre: form.nombre,
      numero_whatsapp: form.numero_local.trim()
        ? `${prefijoAgencia} ${form.numero_local.trim()}`.trim()
        : '',
      correo: form.correo.trim() || null,
      cargo: form.cargo.trim() || null,
      identificacion: form.identificacion.trim() || null,
      es_principal: form.es_principal,
      activo: form.activo,
    };
    if (!editItem) body.agencia_id = agencia.id;
    const url = editItem
      ? `${API_BASE}/turismo/agencias/contactos/${editItem.id}`
      : `${API_BASE}/turismo/agencias/contactos`;
    const res = await fetch(url, {
      method: editItem ? 'PUT' : 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      notify?.(`No se pudo guardar: ${err.detail || 'error desconocido'}`, 'error');
      return;
    }
    setEditing(false);
    notify?.(editItem ? 'Contacto actualizado' : 'Contacto agregado');
    cargar();
  };

  const handleDelete = (id) => {
    const c = contactos.find(x => x.id === id);
    confirmar?.('Eliminar contacto', `¿Eliminar a "${c?.nombre || 'este contacto'}"?`, async () => {
      await fetch(`${API_BASE}/turismo/agencias/contactos/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      notify?.('Contacto eliminado');
      cargar();
    });
  };

  const handleMarcarPrincipal = async (id) => {
    await fetch(`${API_BASE}/turismo/agencias/contactos/${id}/principal`, { method: 'PATCH', headers: getAuthHeaders() });
    cargar();
  };

  // Ordenar: principal primero, luego activos, luego inactivos
  const contactosOrdenados = [...contactos].sort((a, b) => {
    if (a.es_principal && !b.es_principal) return -1;
    if (!a.es_principal && b.es_principal) return 1;
    if (a.activo && !b.activo) return -1;
    if (!a.activo && b.activo) return 1;
    return (a.nombre || '').localeCompare(b.nombre || '');
  });

  return (
    <StyledDialog open onClose={onClose}
      title={editing ? (editItem ? 'Editar Vendedor' : 'Nuevo Vendedor') : `Vendedores — ${agencia.nombre}`}
      icon={<PeopleIcon />} variant="compact" maxWidth="sm">
      <StyledDialog.Body compact>
        {editing ? (
          <>
            <TextField sx={inputStyles} label="Nombre completo" fullWidth required
              value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
            <TextField sx={inputStyles} label="WhatsApp" fullWidth required
              value={form.numero_local}
              onChange={e => setForm({ ...form, numero_local: e.target.value })}
              placeholder="300 000 0000"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 600, color: '#be185d', minWidth: 40 }}>
                      {prefijoAgencia}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`Prefijo ${prefijoAgencia} según el país de la agencia (${agencia.pais || 'Colombia'}). Este es el número por el que escriben al chatbot.`} />
            <TextField sx={inputStyles} label="Correo" type="email" fullWidth
              value={form.correo} onChange={e => setForm({ ...form, correo: e.target.value })} />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField sx={{ ...inputStyles, flex: 1, minWidth: 140 }} label="Cargo"
                value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })}
                placeholder={form.es_principal ? 'Dueño / Representante Legal' : 'Vendedor, Gerente, etc.'} />
              <TextField sx={{ ...inputStyles, flex: 1, minWidth: 140 }} label="Identificación / Cédula"
                value={form.identificacion} onChange={e => setForm({ ...form, identificacion: e.target.value })}
                helperText="Útil para contratos" />
            </Box>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: form.es_principal ? '#fef3c7' : '#f8fafc', border: `1px dashed ${form.es_principal ? '#f59e0b' : '#e5e7eb'}` }}>
              <FormControlLabel
                control={<Switch checked={form.es_principal} onChange={e => setForm({ ...form, es_principal: e.target.checked })} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WorkspacePremiumIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      Es el contacto principal (dueño/representante legal)
                    </Typography>
                  </Box>
                } />
              {form.es_principal && (
                <Typography sx={{ fontSize: 11, color: '#92400e', ml: 6 }}>
                  Al guardar, este pasará a ser el principal y el actual (si hay) dejará de serlo.
                </Typography>
              )}
            </Box>
            <FormControlLabel
              control={<Switch checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} />}
              label={<Typography sx={{ fontSize: 13 }}>Atiende WhatsApp del chatbot {!form.activo && '(solo para contratos, no responde consultas)'}</Typography>} />
          </>
        ) : (
          <>
            <Button variant="contained" startIcon={<AddIcon />} fullWidth
              onClick={() => handleOpenForm()}
              sx={{ background: gradientAgencia, mb: 1 }}>
              Agregar Vendedor
            </Button>
            {contactos.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 3, color: '#64748b' }}>
                <PeopleIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: 13 }}>No hay vendedores. Agrega el primero.</Typography>
              </Box>
            ) : (
              contactosOrdenados.map(c => (
                <Box key={c.id} sx={{
                  p: 1.5, borderRadius: 2,
                  border: c.es_principal ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                  bgcolor: c.es_principal ? '#fffbeb' : '#fff',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  opacity: c.activo || c.es_principal ? 1 : 0.55,
                  position: 'relative',
                }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 2,
                    background: c.es_principal
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      : gradientAgencia,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0,
                  }}>
                    {c.es_principal ? <WorkspacePremiumIcon sx={{ fontSize: 20 }} /> : <PersonIcon sx={{ fontSize: 20 }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#134e4a' }}>
                        {c.nombre}
                      </Typography>
                      {c.es_principal && (
                        <Chip label="Principal" size="small"
                          sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#f59e0b', color: '#fff' }} />
                      )}
                      {!c.activo && (
                        <Chip label="No responde WP" size="small"
                          sx={{ height: 18, fontSize: 9, bgcolor: '#e5e7eb', color: '#64748b' }} />
                      )}
                    </Box>
                    {c.cargo && (
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>{c.cargo}</Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <WhatsAppIcon sx={{ fontSize: 13, color: '#25D366' }} />
                      <Typography sx={{ fontSize: 11, color: '#475569' }}>{c.numero_whatsapp}</Typography>
                    </Box>
                    {c.correo && <Typography sx={{ fontSize: 11, color: '#64748b' }}>{c.correo}</Typography>}
                    {c.identificacion && (
                      <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>ID: {c.identificacion}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {!c.es_principal && (
                      <Tooltip title="Marcar como principal">
                        <IconButton size="small" onClick={() => handleMarcarPrincipal(c.id)}>
                          <StarBorderIcon fontSize="small" sx={{ color: '#f59e0b' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" onClick={() => handleOpenForm(c)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(c.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </Box>
              ))
            )}
          </>
        )}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        {editing ? (
          <>
            <StyledDialog.CancelButton fullWidth onClick={() => setEditing(false)} />
            <StyledDialog.PrimaryButton fullWidth onClick={handleSave}
              disabled={!form.nombre || !form.numero_local}>
              {editItem ? 'Actualizar' : 'Agregar'}
            </StyledDialog.PrimaryButton>
          </>
        ) : (
          <StyledDialog.PrimaryButton fullWidth onClick={onClose}>Cerrar</StyledDialog.PrimaryButton>
        )}
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


// ============================================================================
// DIÁLOGO: COMISIONES POR PAQUETE
// ============================================================================
function ComisionesPaqueteDialog({ agencia, onClose, notify }) {
  const [comisiones, setComisiones] = useState([]);
  const [paquetes, setPaquetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState(null);
  const empresaId = getEmpresaId();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        fetch(`${API_BASE}/turismo/agencias/${agencia.id}/comisiones-paquete`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}&solo_activos=true`, { headers: getAuthHeaders() }),
      ]);
      setComisiones(await cRes.json());
      setPaquetes(await pRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [agencia.id, empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleGuardar = async () => {
    if (!editForm) return;
    const va = parseFloat(editForm.valor_adulto) || 0;
    const vn = parseFloat(editForm.valor_nino)   || 0;
    const isPct = editForm.tipo_comision === 'porcentaje';
    const body = {
      paquete_id: editForm.paquete_id,
      tipo_comision: editForm.tipo_comision,
      porcentaje_comision_adulto: isPct ? va : null,
      porcentaje_comision_nino:   isPct ? vn : null,
      valor_comision_adulto:      isPct ? null : va,
      valor_comision_nino:        isPct ? null : vn,
      porcentaje_comision: isPct ? va : null,
      valor_comision_fijo: isPct ? null : va,
    };
    const res = await fetch(`${API_BASE}/turismo/agencias/${agencia.id}/comisiones-paquete`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
    });
    if (res.ok) {
      notify?.('Comisión guardada');
      setEditForm(null);
      cargar();
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta comisión?')) return; // legacy fallback
    await fetch(`${API_BASE}/turismo/agencias/comisiones-paquete/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    notify?.('Comisión eliminada');
    cargar();
  };

  const comisionesPorPaquete = {};
  comisiones.forEach(c => { comisionesPorPaquete[c.paquete_id] = c; });
  const paquetesSinComision = paquetes.filter(p => !comisionesPorPaquete[p.id]);

  return (
    <StyledDialog open onClose={onClose}
      title={`Comisiones por tour — ${agencia.nombre}`}
      icon={<PercentIcon />} variant="compact" maxWidth="sm">
      <StyledDialog.Body compact>
        <Alert severity="info" sx={{ fontSize: 11, mb: 1 }}>
          Configura la comisión para cada tour. Los tours sin comisión configurada no generan comisión.
        </Alert>

        {loading ? <CircularProgress /> : (
          <>
            {/* Comisiones configuradas */}
            {comisiones.map(c => {
              const isPct = c.tipo_comision === 'porcentaje';
              const valAdulto = isPct
                ? (c.porcentaje_comision_adulto ?? c.porcentaje_comision ?? 0)
                : (c.valor_comision_adulto ?? c.valor_comision_fijo ?? 0);
              const valNino = isPct
                ? (c.porcentaje_comision_nino ?? c.porcentaje_comision ?? 0)
                : (c.valor_comision_nino ?? c.valor_comision_fijo ?? 0);
              const fmtVal = (v) => isPct ? `${v}%` : `$${new Intl.NumberFormat('es-CO').format(v)}`;
              return (
              <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 2,
                bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', mb: 0.75 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{c.paquete_nombre}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#15803d', fontWeight: 700 }}>
                    Adulto: {fmtVal(valAdulto)} · Niño: {fmtVal(valNino)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setEditForm({
                  paquete_id: c.paquete_id,
                  tipo_comision: c.tipo_comision,
                  valor_adulto: String(valAdulto),
                  valor_nino: String(valNino),
                  nombre: c.paquete_nombre,
                })}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton size="small" onClick={() => handleEliminar(c.id)} sx={{ color: '#ef4444' }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              );
            })}

            {/* Tours sin comisión específica */}
            {paquetesSinComision.length > 0 && (
              <>
                <Typography sx={{ fontSize: 11, color: '#64748b', mt: 1, mb: 0.5 }}>
                  Tours sin comisión (no generan comisión):
                </Typography>
                {paquetesSinComision.map(p => (
                  <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2,
                    border: '1px dashed #e5e7eb', mb: 0.5, cursor: 'pointer',
                    '&:hover': { bgcolor: '#f8fafc' } }}
                    onClick={() => setEditForm({ paquete_id: p.id, tipo_comision: 'valor_fijo', valor_adulto: '', valor_nino: '', nombre: p.nombre })}>
                    <Typography sx={{ flex: 1, fontSize: 13, color: '#475569' }}>{p.nombre}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#6366f1' }}>+ Configurar</Typography>
                  </Box>
                ))}
              </>
            )}

            {/* Form de edición inline */}
            {editForm && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e40af', mb: 1 }}>
                  {editForm.nombre}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select value={editForm.tipo_comision}
                      onChange={e => setEditForm({ ...editForm, tipo_comision: e.target.value, valor_adulto: '', valor_nino: '' })}
                      sx={{ fontSize: 12 }}>
                      <MenuItem value="valor_fijo" sx={{ fontSize: 12 }}>$/persona</MenuItem>
                      <MenuItem value="porcentaje" sx={{ fontSize: 12 }}>%</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField size="small" type="number"
                    label="Adulto" InputLabelProps={{ shrink: true }}
                    placeholder={editForm.tipo_comision === 'valor_fijo' ? '100000' : '15'}
                    value={editForm.valor_adulto} onChange={e => setEditForm({ ...editForm, valor_adulto: e.target.value })}
                    sx={{ width: 110, '& input': { fontSize: 13 } }}
                    inputProps={{ min: 0, step: editForm.tipo_comision === 'valor_fijo' ? 1000 : 0.1 }} />
                  <TextField size="small" type="number"
                    label="Niño" InputLabelProps={{ shrink: true }}
                    placeholder={editForm.tipo_comision === 'valor_fijo' ? '50000' : '10'}
                    value={editForm.valor_nino} onChange={e => setEditForm({ ...editForm, valor_nino: e.target.value })}
                    sx={{ width: 110, '& input': { fontSize: 13 } }}
                    inputProps={{ min: 0, step: editForm.tipo_comision === 'valor_fijo' ? 1000 : 0.1 }} />
                  <Button size="small" variant="contained" onClick={handleGuardar}
                    disabled={!editForm.valor_adulto && !editForm.valor_nino}
                    sx={{ textTransform: 'none', fontSize: 12 }}>
                    Guardar
                  </Button>
                  <Button size="small" onClick={() => setEditForm(null)} sx={{ textTransform: 'none', fontSize: 12 }}>
                    Cancelar
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.PrimaryButton fullWidth onClick={onClose}>Cerrar</StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


// ============================================================================
// Modal de enlaces masivos — 3 modos: Lista (botones directos), CSV, Secuencial
// ============================================================================
function BulkEnlacesModal({ data, copiado, onCopiar, onClose }) {
  const [modo, setModo] = React.useState('lista');  // lista | secuencial
  const [secIdx, setSecIdx] = React.useState(0);
  const [marcados, setMarcados] = React.useState({});  // { [agencia_id]: 'enviado_wa' | 'enviado_mail' | 'skip' | 'done' }

  const enlaces = data.enlaces || [];
  const expiraStr = new Date(data.expira_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });

  const buildMensajeWA = (e) => {
    const saludo = e.contacto_principal_nombre ? `Hola ${e.contacto_principal_nombre},` : 'Hola,';
    return `${saludo}
Te escribimos de Entretur.
Para mantener los datos de ${e.agencia_nombre} al día (NIT, dirección, vendedores) usa este enlace:

${e.url}

Vence el ${expiraStr}.`;
  };

  const buildMensajeMail = (e) => {
    return `Hola,

Para mantener los datos de tu agencia "${e.agencia_nombre}" al día en nuestro sistema, te enviamos este enlace seguro y único:

${e.url}

El enlace vence el ${expiraStr}.

En el formulario podrás verificar y actualizar:
- Razón social, NIT, dirección
- Vendedores con su WhatsApp y correo
- Marcar el contacto principal

Tus cambios serán revisados por el equipo de Entretur antes de aplicarse.

Saludos,
Entretur`;
  };

  const sanitizePhone = (s) => String(s || '').replace(/[^\d]/g, '');

  const abrirWhatsApp = (e) => {
    const phone = sanitizePhone(e.contacto_principal_whatsapp || e.telefono_general);
    if (!phone) { alert(`${e.agencia_nombre} no tiene WhatsApp registrado.`); return; }
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(buildMensajeWA(e))}`;
    window.open(url, '_blank');
    setMarcados(m => ({ ...m, [e.agencia_id]: m[e.agencia_id] === 'enviado_mail' ? 'done' : 'enviado_wa' }));
  };

  const abrirCorreo = (e) => {
    const destino = e.contacto_principal_correo || e.correo || '';
    const subject = `Actualiza los datos de ${e.agencia_nombre} en Entretur`;
    const body = buildMensajeMail(e);
    window.location.href = `mailto:${destino}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setMarcados(m => ({ ...m, [e.agencia_id]: m[e.agencia_id] === 'enviado_wa' ? 'done' : 'enviado_mail' }));
  };

  const descargarCSV = () => {
    // CSV con todo lo necesario para mail merge
    const headers = [
      'Agencia', 'NIT', 'Correo agencia', 'Telefono general',
      'Contacto principal', 'WhatsApp principal', 'Correo principal',
      'URL actualizacion', 'wa.me link', 'mailto link',
      'Mensaje WhatsApp', 'Mensaje correo', 'Estado registro', 'Vence',
    ];
    const escapeCSV = (v) => {
      if (v == null) return '';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const rows = enlaces.map(e => {
      const waPhone = sanitizePhone(e.contacto_principal_whatsapp || e.telefono_general);
      const waLink = waPhone ? `https://wa.me/${waPhone}?text=${encodeURIComponent(buildMensajeWA(e))}` : '';
      const mailDestino = e.contacto_principal_correo || e.correo || '';
      const mailtoLink = mailDestino
        ? `mailto:${mailDestino}?subject=${encodeURIComponent(`Actualiza datos de ${e.agencia_nombre} en Entretur`)}&body=${encodeURIComponent(buildMensajeMail(e))}`
        : '';
      return [
        e.agencia_nombre, e.nit_cedula, e.correo, e.telefono_general,
        e.contacto_principal_nombre, e.contacto_principal_whatsapp, e.contacto_principal_correo,
        e.url, waLink, mailtoLink,
        buildMensajeWA(e), buildMensajeMail(e), e.estado_registro, expiraStr,
      ].map(escapeCSV).join(',');
    });
    const csv = '﻿' + [headers.join(','), ...rows].join('\n');  // BOM para Excel detecte UTF-8
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agencias-enlaces-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Modo SECUENCIAL: ver una agencia a la vez
  if (modo === 'secuencial') {
    const e = enlaces[secIdx];
    if (!e) return null;
    const restantes = enlaces.length - secIdx;
    const estadoMarca = marcados[e.agencia_id];
    return (
      <StyledDialog open onClose={onClose} title="Procesar secuencialmente"
        subtitle={`Agencia ${secIdx + 1} de ${enlaces.length} · ${restantes} restantes`}
        icon={<ShareIcon />} maxWidth="sm">
        <StyledDialog.Body>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ height: 6, bgcolor: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{
                height: '100%', width: `${((secIdx + 1) / enlaces.length) * 100}%`,
                bgcolor: '#0d9488', transition: 'width 0.3s',
              }} />
            </Box>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5, mb: 0.5 }}>
              Agencia
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
              {e.agencia_nombre}
            </Typography>
            {e.contacto_principal_nombre && (
              <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                Contacto: {e.contacto_principal_nombre}
              </Typography>
            )}
          </Box>

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Button fullWidth size="large" variant="contained" startIcon={<WhatsAppIcon />}
              disabled={!e.contacto_principal_whatsapp && !e.telefono_general}
              onClick={() => abrirWhatsApp(e)}
              sx={{ py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: 14,
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                    boxShadow: '0 6px 16px rgba(37,211,102,0.35)',
                    '&:hover': { background: 'linear-gradient(135deg, #128C7E 0%, #0a6e64 100%)' } }}>
              {estadoMarca === 'enviado_wa' || estadoMarca === 'done' ? '✓ ' : ''}
              Enviar por WhatsApp
              {e.contacto_principal_whatsapp && (
                <Typography component="span" sx={{ ml: 1, opacity: 0.85, fontSize: 11 }}>
                  ({e.contacto_principal_whatsapp})
                </Typography>
              )}
            </Button>

            <Button fullWidth size="large" variant="contained" startIcon={<MailOutlineIcon />}
              disabled={!(e.contacto_principal_correo || e.correo)}
              onClick={() => abrirCorreo(e)}
              sx={{ py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: 14,
                    background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                    boxShadow: '0 6px 16px rgba(2,132,199,0.35)',
                    '&:hover': { background: 'linear-gradient(135deg, #0369a1 0%, #075985 100%)' } }}>
              {estadoMarca === 'enviado_mail' || estadoMarca === 'done' ? '✓ ' : ''}
              Enviar por correo
            </Button>

            <Button fullWidth size="small" variant="outlined" startIcon={<ContentCopyIcon />}
              onClick={() => onCopiar(e.url, `seq-url-${e.agencia_id}`)}
              sx={{ textTransform: 'none' }}>
              {copiado === `seq-url-${e.agencia_id}` ? '✓ Copiado' : 'Copiar solo el enlace'}
            </Button>
          </Stack>

          <Typography sx={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', mt: 2, fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {e.url}
          </Typography>
        </StyledDialog.Body>
        <StyledDialog.Actions>
          <Button onClick={() => setSecIdx(i => Math.max(0, i - 1))} disabled={secIdx === 0}
            sx={{ textTransform: 'none' }}>← Anterior</Button>
          <Button onClick={() => setModo('lista')} sx={{ textTransform: 'none' }}>Volver a lista</Button>
          {secIdx < enlaces.length - 1 ? (
            <Button variant="contained" disableElevation
              onClick={() => {
                setMarcados(m => ({ ...m, [e.agencia_id]: 'done' }));
                setSecIdx(i => i + 1);
              }}
              sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              Hecho · Siguiente →
            </Button>
          ) : (
            <Button variant="contained" disableElevation
              onClick={() => { setMarcados(m => ({ ...m, [e.agencia_id]: 'done' })); onClose(); }}
              sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}>
              ✓ Terminar
            </Button>
          )}
        </StyledDialog.Actions>
      </StyledDialog>
    );
  }

  // Modo LISTA (default)
  return (
    <StyledDialog open onClose={onClose}
      title={`${enlaces.length} enlaces generados`}
      subtitle="Envía con un click por WhatsApp o correo, o descarga CSV para envío masivo"
      icon={<ShareIcon />}
      maxWidth="md">
      <StyledDialog.Body>
        <Alert severity="success" sx={{ fontSize: 13, mb: 2 }}>
          Todos los enlaces vencen el <b>{expiraStr}</b>. Cualquier enlace anterior fue revocado.
        </Alert>

        {/* Acciones top */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button size="small" variant="contained" disableElevation startIcon={<DownloadIcon />}
            onClick={descargarCSV}
            sx={{ textTransform: 'none', bgcolor: '#0f766e', '&:hover': { bgcolor: '#134e4a' } }}>
            Descargar CSV (para mail merge)
          </Button>
          <Button size="small" variant="outlined" startIcon={<NavigateNextIcon />}
            onClick={() => { setSecIdx(0); setModo('secuencial'); }}
            sx={{ textTransform: 'none', borderColor: '#0d9488', color: '#0d9488' }}>
            Procesar secuencialmente
          </Button>
          <Button size="small" variant="outlined"
            startIcon={copiado === 'bulk-all' ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={() => onCopiar(enlaces.map(e => `${e.agencia_nombre}: ${e.url}`).join('\n'), 'bulk-all')}
            sx={{ textTransform: 'none' }}>
            {copiado === 'bulk-all' ? '✓ Copiado todo' : 'Copiar todas las URL'}
          </Button>
        </Box>

        {/* Lista con botones por agencia */}
        <Box sx={{ maxHeight: 480, overflowY: 'auto', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          {enlaces.map((e, idx) => {
            const enviado = marcados[e.agencia_id] === 'done'
              || marcados[e.agencia_id] === 'enviado_wa'
              || marcados[e.agencia_id] === 'enviado_mail';
            const hasWA = !!(e.contacto_principal_whatsapp || e.telefono_general);
            const hasMail = !!(e.contacto_principal_correo || e.correo);
            return (
              <Box key={e.agencia_id}
                sx={{
                  p: 1.25, borderBottom: idx < enlaces.length - 1 ? '1px solid #f1f5f9' : 'none',
                  bgcolor: enviado ? '#f0fdf4' : 'transparent',
                  display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                }}>
                <Box sx={{
                  width: 26, height: 26, borderRadius: 1, flexShrink: 0,
                  bgcolor: enviado ? '#16a34a' : '#fdf2f8',
                  color: enviado ? '#fff' : '#be185d',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700,
                }}>{enviado ? '✓' : idx + 1}</Box>
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                    {e.agencia_nombre}
                    {e.estado_registro === 'invitada' && (
                      <Chip size="small" label="Invitación"
                        sx={{ ml: 1, height: 16, fontSize: 9, bgcolor: '#e2e8f0', color: '#475569' }} />
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                    {e.contacto_principal_nombre && `${e.contacto_principal_nombre} · `}
                    {e.contacto_principal_whatsapp || e.telefono_general || 'sin WhatsApp'}
                  </Typography>
                </Box>

                <Tooltip title={hasWA ? `Enviar a ${e.contacto_principal_whatsapp || e.telefono_general}` : 'Sin WhatsApp'}>
                  <span>
                    <IconButton size="small" disabled={!hasWA} onClick={() => abrirWhatsApp(e)}
                      sx={{ bgcolor: hasWA ? '#25D366' : '#cbd5e1', color: '#fff',
                            '&:hover': { bgcolor: '#128C7E' },
                            '&:disabled': { color: '#fff' } }}>
                      <WhatsAppIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={hasMail ? `Enviar a ${e.contacto_principal_correo || e.correo}` : 'Sin correo'}>
                  <span>
                    <IconButton size="small" disabled={!hasMail} onClick={() => abrirCorreo(e)}
                      sx={{ bgcolor: hasMail ? '#0284c7' : '#cbd5e1', color: '#fff',
                            '&:hover': { bgcolor: '#0369a1' },
                            '&:disabled': { color: '#fff' } }}>
                      <MailOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Copiar solo el enlace">
                  <IconButton size="small" onClick={() => onCopiar(e.url, `url-${e.agencia_id}`)}
                    sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                    {copiado === `url-${e.agencia_id}`
                      ? <CheckIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                      : <ContentCopyIcon sx={{ fontSize: 16, color: '#475569' }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>

        <Typography sx={{ fontSize: 11, color: '#64748b', mt: 1.5 }}>
          💡 <b>WhatsApp/Correo:</b> abre la app con el mensaje completo prellenado; solo te falta presionar "Enviar".<br/>
          💡 <b>CSV:</b> incluye columnas para mail merge (Gmail + Mailmeteor/YAMM) y enlaces directos wa.me / mailto.<br/>
          💡 <b>Secuencial:</b> ideal para procesar con concentración una a una.
        </Typography>
      </StyledDialog.Body>
      <StyledDialog.Actions>
        <StyledDialog.PrimaryButton onClick={onClose}>Cerrar</StyledDialog.PrimaryButton>
      </StyledDialog.Actions>
    </StyledDialog>
  );
}


/**
 * Celda compacta que muestra "Por tour ▾" en el listado de agencias.
 * Al hacer click abre un popover con la tabla de comisiones por paquete
 * (tarifa por adulto y por niño). Reemplaza la antigua celda que mostraba
 * el campo legacy `porcentaje_comision`/`valor_comision_fijo` (engañoso
 * porque la comisión real vive en comisiones_agencia_paquete).
 */
function ComisionesPorTourCell({ agenciaId }) {
  const [anchor, setAnchor] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const handleOpen = async (e) => {
    setAnchor(e.currentTarget);
    if (data !== null) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/turismo/agencias/${agenciaId}/comisiones-paquete`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const d = await res.json();
        setData(Array.isArray(d) ? d : []);
      } else {
        setData([]);
      }
    } catch {
      setData([]);
    }
    setLoading(false);
  };

  const fmt = (n) => `$${new Intl.NumberFormat('es-CO').format(Number(n) || 0)}`;

  const valorAdulto = (c) =>
    c.tipo_comision === 'valor_fijo'
      ? (c.valor_comision_adulto ?? c.valor_comision_fijo ?? 0)
      : null;
  const valorNino = (c) =>
    c.tipo_comision === 'valor_fijo'
      ? (c.valor_comision_nino ?? c.valor_comision_fijo ?? 0)
      : null;

  return (
    <>
      <Chip
        size="small"
        label={`Por tour ▾`}
        onClick={handleOpen}
        sx={{
          fontSize: 11, fontWeight: 700, cursor: 'pointer',
          bgcolor: '#fdf2f8', color: '#be185d',
          '&:hover': { bgcolor: '#fbcfe8' },
        }}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, maxWidth: 380 } } }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} sx={{ color: '#be185d' }} />
          </Box>
        ) : !data || data.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#94a3b8', p: 1 }}>
            No tiene comisiones por tour configuradas.
          </Typography>
        ) : (
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b',
                                textTransform: 'uppercase', letterSpacing: 0.5,
                                mb: 0.5, pb: 0.5, borderBottom: '1px solid #e2e8f0' }}>
              Comisión por PAX (por tour)
            </Typography>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'collapse',
              '& td, & th': { fontSize: 11, py: 0.5, px: 0.75, textAlign: 'left' },
              '& th': { color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9 },
              '& tbody tr': { borderTop: '1px solid #f1f5f9' },
              '& td.num': { textAlign: 'right', fontWeight: 700, fontFamily: 'monospace' },
            }}>
              <thead>
                <tr><th>Tour</th><th style={{ textAlign: 'right' }}>Adulto</th><th style={{ textAlign: 'right' }}>Niño</th></tr>
              </thead>
              <tbody>
                {data.map(c => (
                  <tr key={c.id}>
                    <td>{c.paquete_nombre || `Paquete ${c.paquete_id}`}</td>
                    <td className="num" style={{ color: '#0f172a' }}>
                      {valorAdulto(c) != null ? fmt(valorAdulto(c)) : `${c.porcentaje_comision_adulto ?? c.porcentaje_comision ?? 0}%`}
                    </td>
                    <td className="num" style={{ color: '#0f172a' }}>
                      {valorNino(c) != null ? fmt(valorNino(c)) : `${c.porcentaje_comision_nino ?? c.porcentaje_comision ?? 0}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
}
